import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  Renderer2,
  computed,
  inject,
  linkedSignal,
  signal,
  viewChild
} from '@angular/core';
import { OrderService } from '../services/order.service';
import { ChoicesComponent } from './choices/choices.component';
import { IconNameType } from '../../shared/icons/icon-name-type.enum';
import { IconComponent } from '../../shared/icons/icon.component';
import { QuantityControlsComponent } from '../../shared/quantity-controls/quantity-controls.component';
import { OrderedItemService } from '../services/ordering/ordered-item.service';
import { ItemEditorService } from '../services/item-editor.service';
import { OrderedItem } from '../models/ordering/ordered-item.model';
import { Subscription, forkJoin, map, mergeMap, of, switchMap, tap } from 'rxjs';
import { OrderUpsellService } from '../services/order-upsell.service';
import { AccountCouponsService } from '../services/account/account-coupons.service';
import { AsyncButtonDirective } from '../../shared/directives/async-button.directive';
import { ImageFallbackDirective } from '../../shared/directives/image-fallback.directive';
import { Router } from '@angular/router';
import { StoreIdService } from '../services/store-id.service';
import { ConfigService } from '../services/config.service';
import { SpecialInstructionsAccordionComponent } from '../../shared/special-instructions-accordion/special-instructions-accordion.component';
import { CommonModule } from '@angular/common';
import { OrderedIngredient } from '../models/ordering/ordered-ingredient.model';
import { ModalService, ModalName } from '../services/modal.service';
import { ItemEditorHeaderComponent } from "./item-editor-header/item-editor-header.component";
import { SelectedChoiceForDescription } from '../models/ordering/ordered-choice.model';
import { ErrorsService } from '../services/errors.service';
import { TranslocoService } from '@ngneat/transloco';
import { ScrollToTopComponent } from "../../shared/scroll-to-top/scroll-to-top.component";
import { UpsellsComponent } from '../../shared/upsells/upsells.component';
import { GTMService } from '../services/gtm.service';
import { EcommerceTriggeringArea } from '../models/ecommerce/ecommerce-triggering-area.enum';
import { EcommerceTriggeringPoint } from '../models/ecommerce/ecommerce-triggering-point.enum';
import { AuditService } from '../services/audit.service';
import { A11yModule } from '@angular/cdk/a11y';
import { SizePrice } from '../models/menu/size-price.model';
import { PanelItem } from '../models/menu/panel-item.model';
import { MenuService } from '../services/menu.service';
import { Utils } from '../utils/utils';
import { TranslocoDirective } from '@ngneat/transloco';

@Component({
  selector: 'cwo-item-editor',
  standalone: true,
  imports: [
    ChoicesComponent,
    IconComponent,
    QuantityControlsComponent,
    UpsellsComponent,
    AsyncButtonDirective,
    SpecialInstructionsAccordionComponent,
    CommonModule,
    ItemEditorHeaderComponent,
    ScrollToTopComponent,
    A11yModule,
    TranslocoDirective,
    ImageFallbackDirective,
],
  templateUrl: './item-editor.component.html',
  styleUrl: './item-editor.component.scss',
})
export class ItemEditorComponent implements AfterViewInit, OnInit, OnDestroy {
  orderService = inject(OrderService);
  modalService = inject(ModalService);
  orderedItemService = inject(OrderedItemService);
  orderUpsellService = inject(OrderUpsellService);
  itemEditorService = inject(ItemEditorService);
  accountCouponsService = inject(AccountCouponsService);
  router = inject(Router);
  storeIdService = inject(StoreIdService);
  configService = inject( ConfigService);
  cd = inject(ChangeDetectorRef);
  transloco = inject(TranslocoService);
  gtmService = inject(GTMService);
  private menuService = inject(MenuService);
  private auditService = inject(AuditService);

  loadingOrderableItem = signal(false);
  upsellsLoading = signal(false);
  previewPanelItem = signal<PanelItem | null>(null);

  previewPrice = computed(() => {
    const panel = this.previewPanelItem();
    if (!panel?.sizePrices?.length) return null;
    const defaultSize = Utils.getDefaultSizePrice(panel.sizePrices);
    return Number(defaultSize?.price).toFixed(2);
  });
  picture = computed(() => {
    return this.orderableItem()?.pictures[0] ?? null;
  });
  iconNameType = IconNameType;
  upsellItems = computed(() => this.orderUpsellService.itemEditorUpsellItems());
  shouldDisplayImage = computed(() => !!this.picture());
  showImageHeader = computed(() => {
    const pic = this.picture();
    return this.shouldDisplayImage() && !!pic?.src && pic?.name !== 'none';
  });
  // ! Fixme this should be handled differently
  markedAsEditor = computed(() => this.itemEditorService.markedAsEditor());
  showSisterUpsells = computed(() =>
    this.itemEditorService.showSisterUpsells()
  );
  triggeringPoint = computed(() =>
    this.itemEditorService._editingItem()?.triggeringPoint
  );

  triggeringArea = computed(() =>
    this.itemEditorService._editingItem()?.triggeringArea
  );
  orderableItem = computed(
    () => this.itemEditorService._editingItem()?.orderableItem
  );
  orderedItem = computed(
    () => this.itemEditorService._editingItem()?.orderedItem
  );
  
  caloriesDisclaimer = this.orderableItem()?.caloriesDisclaimer ?? this.configService.configState()?.store?.caloriesDisclaimer;

  hasCalories = computed(() => {
      const sizePrices = this.orderableItem()?.sizePrices;
      if (!sizePrices) return false;
      const sizeWithCalories = sizePrices.find((s: SizePrice) => s.caloriesFrom != null );
      if (!sizeWithCalories) return false;
      return true;
    });

  itemPrice = computed(() => this.orderedItemService.itemPrice().toFixed(2));

  get hasLoyalty(): boolean {
    return !!this.configService.configState().store?.hasLoyalty &&
      !this.configService.configState().store?.hasThirdPartyLoyalty;
  }

  itemLoyaltyPoints = computed(() => {
    const pointsPerDollar = this.configService.loyaltyState()?.subscriptionsPointsFactors?.[0]?.pointsPerDollar;
    if (!this.hasLoyalty || pointsPerDollar == null) return null;
    return Math.round(this.orderedItemService.itemPrice() * pointsPerDollar);
  });
  toggleWarningsAfterSubmission = false;
  firstInvalidChoiceAfterSubmission = '';
  loadingSubscription: Subscription | null = null;
  quantityLoadingSubscription: Subscription | undefined;
  itemInstructions: string = '';
  sisUpsellHeadingContentHeightDiff: string = '';
  aboveStickyLevel: boolean = false;
  errorsService = inject(ErrorsService);

  currentSizePrice = computed(() => {
    const size = this.orderedItem()?.size;
    return this.orderableItem()?.sizePrices?.find((sp) => sp.size === size);
  });
  maxQuantity = linkedSignal(() => this.currentSizePrice()?.remainingQuantity ?? 99);
  
  defaultIngredients: OrderedIngredient[] = [];
  private resizeObserver: ResizeObserver = new ResizeObserver(() => { return; });
  private mutationObserver: MutationObserver = new MutationObserver(() => { return; });
  scrollToBottomOffset = 0;

  selectedIngredients = computed<SelectedChoiceForDescription[]>(() => {
    return [
      ...this.defaultIngredients.map((ingredient) => ({
        label: ingredient.ingredient,
        removed: !this.orderedItem()?.choices.flatMap((choice) => choice.ingredients).find((ing) => ing.ingredient === ingredient.ingredient),
      })),
      ...this.orderedItem()?.choices.flatMap((choice) => choice.ingredients.filter((ingredient) => !this.defaultIngredients.find((defaultIngredient) => defaultIngredient.ingredient === ingredient.ingredient))
      .map((ingredient) => ({
        label: ingredient.ingredient,
        removed: false,
      }))) ?? []
    ]
    .sort((a, b) => a.removed === b.removed ? 0 : b.removed ? 1 : -1);
  });

  getSelectedIngredientDescription(ingredient: SelectedChoiceForDescription): string {
    const orderedIngredient = this.orderedItem()?.choices.flatMap((choice) => choice.ingredients).find((ing) => ing.ingredient === ingredient.label);

    let descrtiption = '';
    if(!orderedIngredient) {
      return ingredient.label ?? '';
    }

    if(orderedIngredient.isRightHalf && !orderedIngredient.isLeftHalf){
      descrtiption += 'Right half ';
    }

    if(orderedIngredient.isLeftHalf && !orderedIngredient.isRightHalf){
      descrtiption += 'Left half ';
    }

    if(orderedIngredient.qualifiers.length){
      descrtiption += orderedIngredient.qualifiers.join(' ');
      descrtiption += ' ';
    }

    if(orderedIngredient.secondHalfQualifiers.length){
      descrtiption += orderedIngredient.secondHalfQualifiers.join(' ');
      descrtiption += ' ';
    }
    
    descrtiption += orderedIngredient.ingredient;

    return descrtiption;

  }

  readonly choicesWrapper = viewChild<ElementRef>('choicesWrapper');
  readonly footer = viewChild<ElementRef>('footer');
  readonly itemEditorScrollableWrapper = viewChild<ElementRef>('itemEditorScrollableWrapper');
  readonly itemInformationContainer = viewChild<ElementRef>('itemInformationContainer');
  
  readonly sisterUpsellsComp = viewChild<UpsellsComponent>('sisterUpsells');
  readonly sisterUpsellsRef = viewChild<UpsellsComponent, ElementRef>('sisterUpsells', { read: ElementRef });

  private renderer = inject(Renderer2);

  isEmptyBody = computed(() => {
    return this.orderableItem()?.choices.length === 0 && !this.orderService.hasMultipleSizePrices(this.orderableItem());
  });

  ngOnInit(): void {
    
    this.orderUpsellService.setItemEditorUpsellItems([]);
      
      const modalState = this.modalService.stateForModal(ModalName.ItemEditor);
      const panelItem: PanelItem | undefined = modalState?.panelItem;

      if (this.orderableItem() && this.orderedItem()) {
        this.upsellsLoading.set(true);
        this.initializeExistingState();
        this.fetchUpsellsIfNeeded();
        return;
      }

      if (panelItem) {
        this.loadFromPanelItem(panelItem, modalState);
        return;
      }
    }

  private initializeExistingState(): void {
    this.itemInstructions = this.orderedItem()?.instructions ?? '';

    this.defaultIngredients = this.orderedItem()?.choices.flatMap((choice) => choice.ingredients) ?? [];

    const orderableItem = this.orderableItem();
    const orderedItem = this.orderedItem();

    if (!this.modalService.stateForModal(ModalName.ItemEditor)?.openedFromCart) {
      this.auditService.createAudit(() => `Default quantity ${orderableItem?.minimumQuantity ?? orderableItem?.quantity} was set for item ${orderableItem?.label}`);
    }

    if (orderableItem && orderedItem) {
      const modalContent = document.getElementById(
        'item-editor-scrollable-wrapper'
      );
      const footer = document.getElementById('item-editor-footer');

      if (modalContent && footer) {
        modalContent.style.marginBottom = `${
          footer.getBoundingClientRect().height
        }px`;
      }
    }
    // do this to prevent the GTM from blocking the load of the component
    setTimeout(() => {
      if (!orderableItem) {
        return;
      }
      this.gtmService.pushViewItemEvent(
        orderableItem,
        orderedItem?.size ?? orderableItem?.sizes[0],
        this.orderedItemService.itemPrice(),
        orderedItem?.quantity ?? orderableItem?.minimumQuantity ?? 1,
        this.triggeringPoint() ?? '',
        this.triggeringArea(),
      );
    });
  }

  private loadFromPanelItem(panelItem: PanelItem, modalState: any): void {
    this.previewPanelItem.set(panelItem);
    this.loadingOrderableItem.set(true);
    const { triggeringPoint, triggeringArea, forceOpenModal } = modalState;

    this.menuService
      .getOrderableItemFromUrl(panelItem, triggeringPoint, triggeringArea)
      .pipe(
        switchMap((orderedItem) => {
          // isEmptyBodyitem scenario: forceOpenModal on an item already in the cart
          const itemFromOrder = this.orderService.order().items?.find(
            (orderItem) => orderItem.item === orderedItem.item
          );
          if (forceOpenModal && itemFromOrder) {
            this.itemEditorService.updateMarkedState(true);
            const orderableObs =
              this.itemEditorService.getOrderableFromOrderedItemUrl(
                itemFromOrder,
                triggeringPoint,
                triggeringArea,
                true
              ) ?? of(orderedItem);
            const priceObs = this.orderedItemService.getPriceWithIngredients(itemFromOrder);
            return forkJoin([orderableObs, priceObs]).pipe(map(() => orderedItem));
          }
          return of(orderedItem);
        })
      )
      .subscribe({
        next: () => {
          this.upsellsLoading.set(true);
          this.loadingOrderableItem.set(false);
          this.previewPanelItem.set(null);
          this.cd.detectChanges();
          this.initializeExistingState();
          this.fetchUpsellsIfNeeded();
        },
        error: (err) => {
          this.loadingOrderableItem.set(false);
          this.handleLoadError(err);
        },
      });
  }

  private handleLoadError(err: any): void {
    const maxCountdown = this.tryGetStockLimitQuantity(err);
    if (maxCountdown !== undefined) {
      this.showStockLimitModal(maxCountdown);
      this.modalService.closeModal(ModalName.ItemEditor);
      return;
    }

    const parsed = this.errorsService.parseErrorResponse(err);
    const msg = parsed.allErrors[0] ?? this.transloco.translate('modals.generic_error_modal.message');
    this.modalService.openGenericModal({
      modalTitle: this.transloco.translate('modals.generic_error_modal.title'),
      modalMessage: msg,
      isAlert: true,
      confirmButtonText: this.transloco.translate('modals.generic_error_modal.close'),
    });
    this.modalService.closeModal(ModalName.ItemEditor);
  }

  private fetchUpsellsIfNeeded(): void {
    const orderable = this.orderableItem();
    const ordered = this.orderedItem();
    if (!orderable || !ordered) return;

    this.upsellsLoading.set(true);
    this.orderUpsellService.getUpsells(orderable, ordered, true).subscribe({
      complete: () => this.upsellsLoading.set(false),
      error: () => this.upsellsLoading.set(false),
    });
  }

ngAfterViewInit(): void {
  const scrollableElement = this.itemEditorScrollableWrapper()?.nativeElement;
  const choicesWrapper = this.choicesWrapper()?.nativeElement;

  if (!scrollableElement) {
    return;
  }

  this.resizeObserver = new ResizeObserver(() => {
    this.calculateSisUpsellsPosition();
  });

  this.mutationObserver = new MutationObserver(() => {
    const sisterUpsellsRef = this.sisterUpsellsRef();
    if (!sisterUpsellsRef?.nativeElement) {
      return;
    }

    const sisterUpsellsRect = sisterUpsellsRef.nativeElement.getBoundingClientRect();
    if (!sisterUpsellsRect.height) {
      return;
    }

    const mobileOffset = this.isMobile() ? 12 : 0;
    this.sisUpsellHeadingContentHeightDiff = `${36 + mobileOffset - sisterUpsellsRect.height - 5}px`;
    if (this.isMobile()) {
      setTimeout(() => {
        this.calculateSisUpsellsPosition();
      },100);
    }

    this.mutationObserver.disconnect();
  });

  this.renderer.listen(scrollableElement, 'scroll', () => {
    this.calculateSisUpsellsPosition();
  });

  this.resizeObserver.observe(scrollableElement);

  if (choicesWrapper) {
    this.resizeObserver.observe(choicesWrapper);
  }

  this.mutationObserver.observe(scrollableElement, {
    childList: true,
    subtree: true
  });

  if (this.isMobile()) {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.calculateSisUpsellsPosition();
      });
    });
  }
  this.cd.detectChanges();
}
  private isMobile(): boolean {
    return window.innerWidth <= 768;
  }

  ngOnDestroy(): void {
    this.itemEditorService.destroyState();
    this.orderedItemService.resetPrice();
    this.loadingSubscription?.unsubscribe();
    this.resizeObserver.disconnect();
    this.mutationObserver.disconnect();
    this.itemEditorService.updateMarkedState(false);
    if (this.upsellItems().length === 0) {
      this.orderUpsellService.setItemEditorUpsellItems([]);
    }
  }

  closeModal() {
    this.modalService.closeModal();
    this.itemEditorService.handleVisibleSisterUpsells(true);
  }

  determinePrice(orderedItem: OrderedItem) {
    this.orderedItemService.getPriceWithIngredients(orderedItem).subscribe();
  }

  changeQuantity(value: number) {
    const orderedItem = this.orderedItem()?.copy();
    if (!orderedItem) return;
    const previousQuantity = orderedItem.quantity;

    orderedItem.quantity = +value;
    this.quantityLoadingSubscription = this.itemEditorService.updateOrderedItem(orderedItem)?.subscribe({
      next: () => {
        this.auditService.createAudit(() => `Quantity changed to ${orderedItem.quantity} for item ${this.orderedItem()?.item}`);
      },
      error: (error) => {
        const maxCountdown = this.tryGetStockLimitQuantity(error);
        if (maxCountdown !== undefined) {
          orderedItem.quantity = previousQuantity;
          this.maxQuantity.set(previousQuantity);
          this.itemEditorService.updateOrderedItem(orderedItem, false)?.subscribe();
          this.showStockLimitModal(maxCountdown);
        }
      }
    });
  }

  private tryGetStockLimitQuantity(error: any): number | undefined {
    const errors = this.errorsService.parseErrorResponse(error).allErrors;
    const errorMessage = errors.length > 0 ? errors[0] : undefined;
    if (!errorMessage || !this.errorsService.isStockLimitError(errorMessage)) {
      return undefined;
    }
    return this.errorsService.getAvailableQuantityFromError(errorMessage);
  }

  private showStockLimitModal(maxCountdown: number) {
    const itemLabel = this.orderableItem()?.label
      ?? this.orderableItem()?.name
      ?? this.previewPanelItem()?.label
      ?? this.previewPanelItem()?.name;
    this.modalService.openGenericModal({
      modalTitle: maxCountdown === 0
        ? this.transloco.translate('shared_components.out_of_stock.title')
        : this.transloco.translate('shared_components.max_quantity.title'),
      modalMessage: maxCountdown === 0
        ? this.transloco.translate('shared_components.out_of_stock.message', {item: itemLabel})
        : this.transloco.translate('shared_components.max_quantity.message', {quantity: maxCountdown, item: itemLabel}),
      isAlert: true,
    });
  }

  addItemInstructions() {
    const maxInstructionsLength = this.configService.configState().store?.maxSpecialInstructionsLength;
    const orderedItem = this.orderedItem()?.copy();
    
    const sizePrice = this.orderableItem()?.sizePrices?.find(sp => sp.size === this.orderedItem()?.size);
      const noticePeriod = sizePrice?.noticePeriod && sizePrice.noticePeriod > 0
        ? sizePrice.noticePeriod
        : undefined;

    if(orderedItem?.noticePeriod) orderedItem.noticePeriod = noticePeriod;

    if(this.itemInstructions.length <= maxInstructionsLength && this.itemInstructions.length > 0) {
      orderedItem.instructions = this.itemInstructions;  
    }

    return orderedItem;
  }

  addToOrder() {
    this.auditService.createAudit(() => "Add to order button clicked");

    if (!this.orderService.order().hasOrderType) {
      this.auditService.createAudit(() => "Order type not set. Opening order details modal to select order type.");
      this.modalService.updateModalState(ModalName.OrderDetails, { showOrderEditor: true });
      return;
    }

    if (
      this.orderedItemService.isValid(
        this.orderedItem()!,
        this.orderableItem()!
      )
    ) {

      const orderedItem = this.addItemInstructions();

      if (this.itemEditorService.isItemEditing()) {
        this.loadingSubscription = this.orderedItemService
          .updateItem(orderedItem).pipe(
            tap((orderedItem) => {
              this.gtmService.pushAddToCartEvent({
                order: this.orderService.order(),
                items: [orderedItem],
                triggeringArea: EcommerceTriggeringArea.ITEM_EDITOR,
                triggeringPoint: EcommerceTriggeringPoint.UPDATE_BUTTON,
              });        
            }),
          )
          .subscribe({
            next: () => {
              this.auditService.createAudit(() => `'${orderedItem.size} ${orderedItem.item}' item has been updated in the order: ${JSON.stringify({ itemId: orderedItem.itemId })}`);
            },
            error: (error) => {
              this.loadingSubscription = null;
              const maxCountdown = this.tryGetStockLimitQuantity(error);
              if (maxCountdown !== undefined) {
                this.showStockLimitModal(maxCountdown);
              }
            },
            complete: () => {
              this.loadingSubscription = null;
              this.closeModalAndOpenOrderTreeNotification();              
            },
          });
      } else {
        const orderedItem = this.addItemInstructions();
        this.loadingSubscription = this.orderService
          .addItemToOrder(orderedItem)
          .pipe(
            tap((orderedItem) => {
              this.orderedItemService.updateSelectedItem(orderedItem);
              this.gtmService.pushAddToCartEvent({
                order: this.orderService.order(),
                items: [orderedItem],
                triggeringArea: EcommerceTriggeringArea.ITEM_EDITOR,
                triggeringPoint: EcommerceTriggeringPoint.ORDER_BUTTON,
              });
              this.auditService.createAudit(() => `${orderedItem.quantity} amount of ${orderedItem.item} item has been added to the order: { itemId: ${orderedItem.itemId}, isUpsell: ${orderedItem.isUpsell} }`);
            }),
            mergeMap(() => this.orderService.fetchOrder())
          )
          .subscribe({
            error: (error) => {
              this.loadingSubscription = null;
              const maxCountdown = this.tryGetStockLimitQuantity(error);
              if (maxCountdown !== undefined) {
                this.showStockLimitModal(maxCountdown);
              }
            },
            complete: () => {
              this.loadingSubscription = null;
              if(!this.router.url.includes(`${this.storeIdService.getStorePathPrefix()}/menu`)) {
                this.router.navigate([this.storeIdService.getStorePathPrefix(), 'menu']);
              }
              this.closeModalAndOpenOrderTreeNotification();
            },
          });
      }
    } else {
      this.toggleWarningsAfterSubmission = !this.orderedItemService.isValid(
        this.orderedItem()!,
        this.orderableItem()!
      );
      const firstInvalidChoice = this.orderedItem()?.choices.find(
        (c) => c.warnings?.length
      );
      this.auditService.createAudit(() => `${this.orderableItem()?.name} could not be added to order due to invalid choices: ${firstInvalidChoice?.warnings?.join(', ')}`);
      this.firstInvalidChoiceAfterSubmission = firstInvalidChoice?.name ?? '';
    }
  }

  closeModalAndOpenOrderTreeNotification() {
    this.modalService.closeModal().subscribe({
      complete: () => {
        if(!this.router.url.includes(`${this.storeIdService.getStorePathPrefix()}/checkout`)) {
          this.modalService.openModal(ModalName.OrderTreeNotification);
        }
      }
    })
  }

  resetWarningsAfterSubmissionValue() {
    this.firstInvalidChoiceAfterSubmission = '';
  }

  onInstructionsChange(value: string): void {
    this.itemInstructions = value;
  }

  calculateSisUpsellsPosition() {
    const sisterUpsellsRef = this.sisterUpsellsRef();
    const sisterUpsellsComp = this.sisterUpsellsComp();
    const footer = this.footer();
    if (!sisterUpsellsComp || !sisterUpsellsRef?.nativeElement || !footer?.nativeElement) {
      return;
    }
    const sisterUpsellsRect = sisterUpsellsRef.nativeElement.getBoundingClientRect();
    const footerRect = footer.nativeElement.getBoundingClientRect();
    if (!sisterUpsellsRect || sisterUpsellsRect.height === 0) {
      return;
    }
    const availableSpace = footerRect.top - sisterUpsellsRect.top;
    const shouldCollapse = availableSpace < 100;
    if (!shouldCollapse) {
      this.aboveStickyLevel = false;
      sisterUpsellsComp.expanded = true;
      this.scrollToBottomOffset = 0;
    } else {
      this.aboveStickyLevel = true;
      sisterUpsellsComp.expanded = false;
      if (this.showSisterUpsells() && this.sisUpsellHeadingContentHeightDiff) {
        const bottomValue = parseFloat(this.sisUpsellHeadingContentHeightDiff);
        const visibleUpsellsHeight = sisterUpsellsRect.height + bottomValue;
        this.scrollToBottomOffset = Math.max(0, Math.ceil(visibleUpsellsHeight) - 1);
      } else {
        this.scrollToBottomOffset = 35;
      }
    }
    this.cd.detectChanges();
  }

  openImagePreviewModal() {
    this.modalService.openModal(ModalName.ImagePreview, {
      imageSrc: this.picture()?.src ?? ''
    })
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    if(event) {
      this.calculateSisUpsellsPosition();
    }
  }
}
